/**
 * usePaperSummary Hook
 * Manages paper summary state, job tracking, and real-time updates
 *
 * Automatically:
 * - Checks for existing summary or job on mount
 * - Subscribes to job updates when job is active
 * - Fetches summary when job completes
 * - Cleans up subscriptions on unmount
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { PaperSummary, PaperSummaryJob } from '../components/papers/types';
import * as paperSummaryService from '../lib/paperSummaryService';
import type { RealtimeChannel } from '@supabase/supabase-js';
import {
    buildSummaryLimitMessage,
    isSummaryLimitReachedError,
    preflightSummaryGeneration,
    toSummaryLimitReachedError,
} from '../lib/entitlements';
import { canAccessMainApp } from '../utils/authHelpers';
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from '../lib/i18n/messages';
import { useSession } from '../lib/SessionContext';
import {
    resolveEnqueuePreferredLanguageTag,
    type ProfileLanguageClient,
} from '../lib/i18n/emailEnqueuePreference';

interface UsePaperSummaryReturn {
    summary: PaperSummary | null;
    job: PaperSummaryJob | null;
    status: 'not_requested' | 'queued' | 'processing' | 'completed' | 'failed' | 'available' | 'loading';
    loading: boolean;
    error: LocalizedMessage | null;
    summaryLimitReached: boolean;
    generateSummary: () => Promise<void>;
    retry: () => Promise<void>;
}

export function usePaperSummary(
    paperId: string | null | undefined,
    projectId: string | null | undefined
): UsePaperSummaryReturn {
    const { profile } = useSession();
    const [summary, setSummary] = useState<PaperSummary | null>(null);
    const [job, setJob] = useState<PaperSummaryJob | null>(null);
    const [status, setStatus] = useState<'not_requested' | 'queued' | 'processing' | 'completed' | 'failed' | 'available' | 'loading'>('loading');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<LocalizedMessage | null>(null);
    const [summaryLimitReached, setSummaryLimitReached] = useState(false);

    const subscriptionRef = useRef<RealtimeChannel | null>(null);

    // Check summary status on mount
    useEffect(() => {
        // Skip if paperId is not available yet
        if (!paperId) {
            setLoading(false);
            return;
        }

        checkStatus();

        // Cleanup subscription on unmount
        return () => {
            if (subscriptionRef.current) {
                console.log('[usePaperSummary] Cleaning up subscription');
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
            }
        };
    }, [paperId]);

    const checkStatus = async () => {
        if (!paperId) return;

        try {
            setLoading(true);
            setError(null);
            setSummaryLimitReached(false);

            const statusResult = await paperSummaryService.getSummaryStatus(paperId);

            if (statusResult.status === 'available') {
                setSummary(statusResult.summary);
                setJob(null);
                // i18n-ignore: technical-token -- persisted summary state identifier
                setStatus('available');
            } else if (statusResult.status === 'queued' || statusResult.status === 'processing') {
                setJob(statusResult.job);
                setSummary(null);
                setStatus(statusResult.status);
                // Subscribe to job updates
                subscribeToJobUpdates(statusResult.job.id);
            } else if (statusResult.status === 'failed') {
                setJob(statusResult.job);
                setSummary(null);
                // i18n-ignore: technical-token -- persisted summary state identifier
                setStatus('failed');
            } else {
                setSummary(null);
                setJob(null);
                // i18n-ignore: technical-token -- persisted summary state identifier
                setStatus('not_requested');
            }
        } catch (err: any) {
            console.error('[usePaperSummary] Error checking status:', err);
            setError(msg({
                id: 'summary.error.checkFailed',
                message: 'Failed to check summary status',
                comment: 'Generic summary status error; raw provider errors remain in logs.',
            }));
        } finally {
            setLoading(false);
        }
    };

    const subscribeToJobUpdates = (jobId: string) => {
        // Unsubscribe from previous subscription if any
        if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
        }

        subscriptionRef.current = paperSummaryService.subscribeToJob(jobId, async (updatedJob) => {
            console.log('[usePaperSummary] Job updated:', updatedJob.status);
            setJob(updatedJob);

            if (updatedJob.status === 'completed') {
                // Fetch the completed summary
                try {
                    if (paperId) {
                        const fetchedSummary = await paperSummaryService.fetchSummary(paperId);
                        if (fetchedSummary) {
                            setSummary(fetchedSummary);
                            // i18n-ignore: technical-token -- persisted summary state identifier
                            setStatus('available');
                        }
                    }
                } catch (err: any) {
                    console.error('[usePaperSummary] Error fetching summary:', err);
                    setError(msg({
                        id: 'summary.error.fetchFailed',
                        message: 'Failed to fetch completed summary',
                        comment: 'Generic completed-summary loading error; raw errors remain in logs.',
                    }));
                }
                // Cleanup subscription after completion
                if (subscriptionRef.current) {
                    subscriptionRef.current.unsubscribe();
                    subscriptionRef.current = null;
                }
            } else if (updatedJob.status === 'failed') {
                // i18n-ignore: technical-token -- persisted summary state identifier
                setStatus('failed');
                // Cleanup subscription after failure
                if (subscriptionRef.current) {
                    subscriptionRef.current.unsubscribe();
                    subscriptionRef.current = null;
                }
            } else {
                setStatus(updatedJob.status as 'queued' | 'processing');
            }
        });
    };

    const generateSummary = useCallback(async () => {
        if (!paperId) {
            setError(msg({ id: 'summary.error.paperUnavailable', message: 'Paper ID is not available' }));
            return;
        }

        try {
            setError(null);
            setSummaryLimitReached(false);

            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user) {
                setError(msg({ id: 'summary.error.signInRequired', message: 'You must be logged in to generate summaries' }));
                return;
            }

            if (session.access_token && canAccessMainApp(session)) {
                try {
                    await preflightSummaryGeneration(session.access_token);
                } catch (preflightError) {
                    if (isSummaryLimitReachedError(preflightError)) {
                        throw toSummaryLimitReachedError(preflightError);
                    }

                    // Keep the entitlement endpoint from becoming a hard dependency.
                    // The database trigger remains the authoritative enforcement point.
                    console.warn('[usePaperSummary] Summary entitlement preflight failed; falling back to DB enforcement:', preflightError);
                }
            }

            console.log('[usePaperSummary] Generating summary...');
            const preferredLanguageTag = await resolveEnqueuePreferredLanguageTag({
                client: supabase as unknown as ProfileLanguageClient,
                userId: session.user.id,
                loadedProfile: profile,
            });
            const newJob = await paperSummaryService.createSummaryJob(
                paperId,
                projectId || null,
                session.user.id,
                preferredLanguageTag
            );

            setJob(newJob);
            // i18n-ignore: technical-token -- persisted summary state identifier
            setStatus('queued');

            // Subscribe to job updates
            subscribeToJobUpdates(newJob.id);
        } catch (err: any) {
            console.error('[usePaperSummary] Error generating summary:', err);
            const resolvedError = isSummaryLimitReachedError(err)
                ? toSummaryLimitReachedError(err)
                : err;
            const reachedLimit = isSummaryLimitReachedError(resolvedError);
            setSummaryLimitReached(reachedLimit);
            setError(reachedLimit
                ? buildSummaryLimitMessage(resolvedError.snapshot)
                : msg({
                    id: 'summary.error.createFailed',
                    message: 'Failed to create summary job',
                    comment: 'Generic summary creation error; raw provider errors remain in logs.',
                }));
        }
    }, [paperId, profile?.preferred_language_tag, projectId]);

    const retry = useCallback(async () => {
        console.log('[usePaperSummary] Retrying summary generation...');
        // Retry is just generating a NEW job (don't modify the failed one)
        await generateSummary();
    }, [generateSummary]);

    return {
        summary,
        job,
        status,
        loading,
        error,
        summaryLimitReached,
        generateSummary,
        retry,
    };
}
